import React, { useState, useRef, useEffect, memo } from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import { isTwoHandDumbbellExercise, formatWeight, formatReps, formatDuration } from '../../utils/weightFormatter';

// 배경 이미지 imports (기존과 동일)
import bg1 from '../../assets/background/bg1.png';
import bg2 from '../../assets/background/bg2.png';
import bg3 from '../../assets/background/bg3.png';
import bg4 from '../../assets/background/bg4.png';
import bg5 from '../../assets/background/bg5.png';
import bgs from '../../assets/background/bgs.png';
import logo from '../../assets/logo.png';

// isActive prop 추가하여 컴포넌트 활성화 여부를 외부에서 제어
const WorkoutSummaryImage = ({ workouts, user, isActive = true, selectedDate }) => {
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [backgroundSize, setBackgroundSize] = useState({ width: 0, height: 0, aspectRatio: 1 });
  const [pages, setPages] = useState([]); // DOM 렌더링 및 계산용 페이지
  const [isLoading, setIsLoading] = useState(true); // 페이지 계산 및 초기 로딩 상태
  const [generationStatus, setGenerationStatus] = useState('idle'); // 이미지 생성 상태: idle, calculating, generating, complete, error
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [generatedImageUrls, setGeneratedImageUrls] = useState([]); // 생성된 PNG 이미지 URL 배열
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentImagePage, setCurrentImagePage] = useState(0);
  const [wasActive, setWasActive] = useState(false); // 이전 활성화 상태 추적
  
  const imageCarouselRef = useRef(null); // 최종 이미지 캐러셀 ref
  const contentRef = useRef(null); // 페이지 계산용 ref
  const domCarouselRef = useRef(null); // 숨겨진 DOM 캐러셀 ref (캡처용)
  const checkDomTimerRef = useRef(null); // DOM 확인 타이머 참조
  const prevWorkoutsRef = useRef(); // 이전 workouts 추적용 ref
  const prevBackgroundSizeRef = useRef(); // 이전 backgroundSize 추적용 ref

  // 배경 이미지 배열 (기존과 동일)
  const backgroundImages = [
    bg1, bg2, bg3, bg4, bg5, bgs,
  ];

  // 랜덤 배경 이미지 가져오기 (기존과 동일)
  const getRandomBackground = () => {
    const randomIndex = Math.floor(Math.random() * backgroundImages.length);
    return backgroundImages[randomIndex];
  };

  // isActive prop이 변경되었을 때 처리
  useEffect(() => {
    // 비활성화 -> 활성화: 생성 프로세스 초기화
    if (isActive && !wasActive) {
      setGenerationStatus('idle');
      setIsLoading(true);
      setWasActive(true);
    } 
    // 활성화 -> 비활성화: 진행 중인 모든 작업 중단
    else if (!isActive && wasActive) {
      setWasActive(false);
      // 진행 중인 타이머 정리
      if (checkDomTimerRef.current) {
        clearTimeout(checkDomTimerRef.current);
        checkDomTimerRef.current = null;
      }
      // 이미지 생성 중이었다면 중단
      if (generationStatus === 'calculating' || generationStatus === 'generating') {
        setGenerationStatus('idle');
      }
    }
  }, [isActive, wasActive, generationStatus]);

  // 1. 컴포넌트 마운트: 배경 선택 및 크기 로드 (isActive 조건 추가)
  useEffect(() => {
    if (!isActive) return; // 비활성 상태면 실행하지 않음
    
    const randomBg = getRandomBackground();
    setSelectedBackground(randomBg);

    const img = new Image();
    img.src = randomBg;
    img.onload = () => {
      setBackgroundSize({ width: img.width, height: img.height, aspectRatio: img.width / img.height });
    };
    img.onerror = (err) => {
      console.error("배경 이미지 로드 실패:", err);
      setErrorMessage("배경 이미지를 로드할 수 없습니다.");
      setGenerationStatus('error');
      setIsLoading(false);
    };
  }, [isActive]);

  // *** Props 변경 시 상태 리셋 (isActive 조건 추가) ***
  useEffect(() => {
    if (!isActive) return; // 비활성 상태면 실행하지 않음

    let propsChanged = false;
    if (prevWorkoutsRef.current !== workouts) {
      propsChanged = true;
      prevWorkoutsRef.current = workouts;
    }
    if (prevBackgroundSizeRef.current?.width !== backgroundSize.width || prevBackgroundSizeRef.current?.height !== backgroundSize.height) {
      propsChanged = true;
      prevBackgroundSizeRef.current = backgroundSize;
    }

    if (propsChanged && generationStatus !== 'idle') {
      setGenerationStatus('idle');
      setIsLoading(true);
      setPages([]);
      setGeneratedImageUrls([]);
      setErrorMessage(null);
    }
  }, [workouts, backgroundSize, generationStatus, isActive]);

  // 2. 운동 데이터/배경 크기 변경: 페이지 계산 시작 (isActive 조건 추가)
  useEffect(() => {
    if (!isActive) return; // 비활성 상태면 실행하지 않음

    if (workouts && backgroundSize.height > 0 && generationStatus === 'idle') {
      setGenerationStatus('calculating');
      setIsLoading(true);
      setGeneratedImageUrls([]);
      setPages([workouts]);
      requestAnimationFrame(() => {
        setTimeout(() => { checkOverflowAndSplit(); }, 100);
      });
    } else if (workouts && workouts.length === 0 && backgroundSize.height > 0 && generationStatus === 'idle') {
      setPages([[]]);
      setGeneratedImageUrls([]);
      setGenerationStatus('complete');
      setIsLoading(false);
    }
  }, [workouts, backgroundSize, generationStatus, isActive]);

  // 3. 페이지 계산 완료 감지 및 이미지 생성 요청 (isActive 조건 추가)
  useEffect(() => {
    if (!isActive) return; // 비활성 상태면 실행하지 않음

    if (!isLoading && generationStatus === 'calculating') {
      if (pages.length > 0 && !(pages.length === 1 && pages[0].length === 0)) {
        setGenerationStatus('generating');
      } else {
        setGenerationStatus('complete');
      }
    }
  }, [isLoading, generationStatus, pages, isActive]);

  // 4. 'generating' 상태 + pages 데이터 안정화 후 이미지 생성 함수 호출 (isActive 조건 추가)
  useEffect(() => {
    if (!isActive) return; // 비활성 상태면 실행하지 않음

    const clearCheckTimer = () => {
      if (checkDomTimerRef.current) {
        clearTimeout(checkDomTimerRef.current);
        checkDomTimerRef.current = null;
      }
    };

    if (generationStatus === 'generating' && pages.length > 0 && !(pages.length === 1 && pages[0].length === 0)) {
      let attempts = 0;
      const maxAttempts = 15;
      const checkInterval = 200;

      const checkDomAndGenerate = () => {
        // 중간에 비활성화되었는지 확인
        if (!isActive) {
          clearCheckTimer();
          return;
        }

        const expectedPages = pages.length;
        const currentRenderedPages = domCarouselRef.current?.children?.length;

        if (domCarouselRef.current && currentRenderedPages === expectedPages) {
          generateAllPagesAsImages().catch(err => {
            console.error("[checkDomAndGenerate]: generateAllPagesAsImages 실행 중 에러:", err);
            setErrorMessage(err.message || "이미지 생성 중 예상치 못한 오류 발생");
            setGenerationStatus('error');
          });
        } else {
          attempts++;
          if (attempts < maxAttempts) {
            checkDomTimerRef.current = setTimeout(checkDomAndGenerate, checkInterval);
          } else {
            console.error(`[checkDomAndGenerate] 최대 시도(${maxAttempts}) 후에도 DOM 준비 안됨. Expected: ${expectedPages}, Got: ${currentRenderedPages}.`);
            setErrorMessage(`이미지 생성 대상 요소(${expectedPages}개)를 준비하지 못했습니다.`);
            setGenerationStatus('error');
          }
        }
      };

      clearCheckTimer();
      checkDomTimerRef.current = setTimeout(checkDomAndGenerate, checkInterval);

      return clearCheckTimer;
    }
    return clearCheckTimer;
  }, [generationStatus, pages, isActive]);

  // *** 신규 Effect: 최종 DOM 상태 확인 ***
  useEffect(() => {
    if (generationStatus === 'complete' && generatedImageUrls.length > 0 && imageCarouselRef.current) {
      setTimeout(() => {
      }, 100);
    }
  }, [generationStatus, generatedImageUrls]);

  // 컴포넌트 로드 완료 시 캐러셀에 포커스 설정 (키보드 네비게이션용)
  useEffect(() => {
    if (generationStatus === 'complete' && generatedImageUrls.length > 1 && imageCarouselRef.current) {
      imageCarouselRef.current.focus();
    }
  }, [generationStatus, generatedImageUrls]);

  // 단순화된 페이지 분할 로직 - 실제 DOM 측정 기반, 페이지 넘침 방지 개선
  const checkOverflowAndSplit = () => {
    const listContainer = contentRef.current;
    if (!listContainer || !workouts || workouts.length === 0) {
      setPages(workouts && workouts.length === 0 ? [[]] : []);
      setIsLoading(false);
      return;
    }

    // 카드 컨테이너와 헤더/타이틀 요소 찾기
    const summaryCardContainer = listContainer.closest('[class^="WorkoutSummaryImage__SummaryCardContainer"]') || 
                               listContainer.parentElement?.parentElement;

    // 컨테이너 실제 높이 측정
    const containerRect = listContainer.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(listContainer);
    const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;
    
    // 헤더/타이틀 높이 측정 - DOM에서 직접 측정
    let headerHeight = 0;
    let firstPageTitleHeight = 0;
    let otherPageTitleHeight = 0;
    
    const headerElement = summaryCardContainer?.querySelector('[data-el="header"]');
    const titleElement = summaryCardContainer?.querySelector('[data-el="title"]');
    
    if (headerElement) {
      headerHeight = headerElement.getBoundingClientRect().height || 0;
    }
    
    if (titleElement) {
      firstPageTitleHeight = titleElement.getBoundingClientRect().height || 0;
      otherPageTitleHeight = 24 + 4; // 1rem + 0.25rem 마진 (DOM 구조에서 확인됨)
    }
    
    // 첫 페이지와 후속 페이지의 가용 높이 계산
    const titleDifference = firstPageTitleHeight - otherPageTitleHeight;
    const firstPageAvailableHeight = containerRect.height - paddingTop - paddingBottom;
    const otherPagesAvailableHeight = firstPageAvailableHeight + titleDifference;
    
    // DOM 요소의 실제 높이 측정 (충분한 시간을 두고 측정)
    setTimeout(() => {      
      // 새로운 접근법: 모든 직계 자식 요소를 워크아웃 컨테이너로 간주
      const allContainers = Array.from(listContainer.children);
      
      // 측정 결과 저장 배열
      const itemPairs = [];
      
      // 각 자식 요소의 첫번째 자식은 WorkoutItem, 두번째 자식은 WorkoutDivider로 간주
      for (let i = 0; i < Math.min(allContainers.length, workouts.length); i++) {
        const container = allContainers[i];
        const workout = workouts[i];
        
        if (container && workout && container.childElementCount > 0) {
          const containerHeight = container.getBoundingClientRect().height;
          
          // 첫번째 자식이 WorkoutItem, 두번째 자식이 WorkoutDivider로 간주
          const workoutItem = container.children[0];
          const workoutDivider = container.children[1];
          
          const itemHeight = workoutItem ? workoutItem.getBoundingClientRect().height : 0;
          const dividerHeight = workoutDivider ? workoutDivider.getBoundingClientRect().height : 0;
          
          if (containerHeight > 0) {
            itemPairs.push({
              workout,
              itemHeight: itemHeight,
              dividerHeight: dividerHeight,
              totalHeight: containerHeight  // 컨테이너 전체 높이 사용
            });
          }
        }
      }
      
      // 워크아웃 항목 또는 이름으로 직접 찾기 (대체 방법)
      if (itemPairs.length < workouts.length) {
        // 1. 모든 요소에서 텍스트 내용으로 워크아웃 이름 찾기
        const allElements = listContainer.querySelectorAll('*');
        const remainingWorkouts = workouts.filter(w => !itemPairs.some(item => item.workout === w));
        
        for (const workout of remainingWorkouts) {
          // 워크아웃 이름과 세트 수 포함된 요소 찾기
          const elementWithWorkoutName = Array.from(allElements).find(el => 
            el.textContent && 
            el.textContent.includes(workout.name) && 
            el.textContent.includes(workout.sets.length + "세트")
          );
          
          if (elementWithWorkoutName) {
            // 부모 요소 찾기 (가장 가까운 직계 자식)
            let containerElement = elementWithWorkoutName;
            while (containerElement && containerElement.parentElement !== listContainer) {
              containerElement = containerElement.parentElement;
              if (!containerElement) break;
            }
            
            if (containerElement) {
              const containerHeight = containerElement.getBoundingClientRect().height;
              
              if (containerHeight > 0) {
                const childCount = containerElement.childElementCount;
                const firstChild = containerElement.children[0];
                const secondChild = containerElement.children[1];
                
                const itemHeight = firstChild ? firstChild.getBoundingClientRect().height : 0;
                const dividerHeight = secondChild ? secondChild.getBoundingClientRect().height : 0;
                
                itemPairs.push({
                  workout,
                  itemHeight,
                  dividerHeight,
                  totalHeight: containerHeight
                });
              }
            }
          }
        }
      }
      
      // 페이지 분할 로직 적용 (실제 측정 항목만 사용)
      const pages = [];
      let currentPage = [];
      let currentHeight = 0;
      const gap = parseFloat(containerStyle.gap) || 4; // 기본값 4px
      
      // 측정된 항목들을 페이지에 분배
      for (let i = 0; i < itemPairs.length; i++) {
        const { workout, totalHeight } = itemPairs[i]; // 컨테이너 전체 높이 사용
        const isLastItem = i === itemPairs.length - 1;
        
        // 첫 항목이 아니면 gap 추가
        const heightWithGap = currentPage.length > 0 ? gap : 0;
        
        // 마지막 항목이면 divider 높이 제외 (이미 totalHeight에 포함됨)
        const dividerHeight = itemPairs[i].dividerHeight;
        const heightNeeded = heightWithGap + (isLastItem ? totalHeight - dividerHeight : totalHeight);
        
        // 현재 페이지가 첫 페이지인지 확인
        const isFirstPage = pages.length === 0;
        const availableHeight = isFirstPage ? firstPageAvailableHeight : otherPagesAvailableHeight;
        
        // 페이지에 항목 추가 가능한지 확인
        if (currentHeight + heightNeeded <= availableHeight) {
          // 현재 페이지에 추가
          currentPage.push(workout);
          currentHeight += heightNeeded;
        } else {
          // 이 항목을 추가하면 넘치는지 확인
          // divider 높이를 제외하고도 넘치는지 확인
          const heightWithoutDivider = heightWithGap + (totalHeight - dividerHeight);
          
          // divider 높이를 제외해도 여전히 넘치면 새 페이지로 이동
          if (currentHeight + heightWithoutDivider > availableHeight) {
            // 페이지가 가득 찼으면 새 페이지 시작
            if (currentPage.length > 0) {
              pages.push([...currentPage]);
              currentPage = [workout];
              // 새 페이지 시작 높이 - 마지막 항목이면 divider 높이 제외
              currentHeight = isLastItem ? totalHeight - dividerHeight : totalHeight;
            } else {
              // 빈 페이지에는 항목을 강제로 추가 (최소 1개)
              currentPage.push(workout);
              pages.push([...currentPage]);
              currentPage = [];
              currentHeight = 0;
            }
          } else {
            // divider 높이를 제외하면 페이지에 들어갈 수 있음
            // 이 경우 현재 페이지의 마지막 항목이 되므로 divider 높이 제외
            currentPage.push(workout);
            currentHeight += heightWithoutDivider;
          }
        }
      }
      
      // 마지막 페이지 추가
      if (currentPage.length > 0) {
        pages.push(currentPage);
      }
      
      setPages(pages.length > 0 ? pages : [[]]);
      setIsLoading(false);
    }, 300); // 충분한 렌더링 시간 확보
  };

  // 모든 페이지 이미지 생성 함수 (내부 로직 변경 없음)
  const generateAllPagesAsImages = async () => {
    const expectedPages = pages.length;
    if (!domCarouselRef.current || !domCarouselRef.current.children || domCarouselRef.current.children.length !== expectedPages) {
        throw new Error(`GenerateAll DOM 불일치 (Expected: ${expectedPages}, Got: ${domCarouselRef.current?.children?.length})`);
    }
    if (!pages || pages.length === 0 || (pages.length === 1 && pages[0].length === 0)) {
      setGenerationStatus('complete');
      return;
    }

    const tempImageUrls = [];
    const totalPages = pages.length;
    setGenerationProgress({ current: 0, total: totalPages });
    try {
      for (let i = 0; i < totalPages; i++) {
        setGenerationProgress({ current: i + 1, total: totalPages });
        const targetNode = domCarouselRef.current.children[i];
        if (!targetNode) { console.error(`Target node ${i + 1} not found! Skipping.`); continue; }

        const scrollWidth = targetNode.offsetWidth || domCarouselRef.current.offsetWidth;
        if (scrollWidth > 0) {
           domCarouselRef.current.scrollTo({ left: i * scrollWidth, behavior: 'instant' });
           await new Promise(resolve => setTimeout(resolve, 100));
        }

        const canvas = await html2canvas(targetNode, {
          useCORS: true, allowTaint: true, backgroundColor: null,
          scale: 3, logging: false, scrollX: 0, scrollY: 0,
        });
        tempImageUrls.push(canvas.toDataURL('image/png'));
      }

      if (tempImageUrls.length > 0) {
          setGeneratedImageUrls(tempImageUrls);
          setGenerationStatus('complete');
      } else if (totalPages > 0) {
          throw new Error("모든 페이지 이미지 생성에 실패했습니다.");
      } else {
          setGenerationStatus('complete');
      }
    } catch (err) {
      console.error(`[GenerateAll] Error during image generation:`, err);
      setErrorMessage(err.message || "이미지 생성 중 오류 발생");
      setGenerationStatus('error');
    }
  };

  // 페이지 이동 함수 - 문제 해결을 위해 수정된 코드
  const goToPage = (pageIndex) => {
    // console.log(`[goToPage] 호출됨: ${currentImagePage} → ${pageIndex}`);
    
    // 유효한 페이지 인덱스인지, 레퍼런스가 있는지 확인
    if (
      pageIndex < 0 || 
      pageIndex >= generatedImageUrls.length || 
      !imageCarouselRef.current
    ) {
      // console.log('[goToPage] 조건 불충족으로 이동하지 않음');
      return;
    }

    // 현재 페이지 업데이트
    setCurrentImagePage(pageIndex);
    
    // 이벤트 충돌 방지를 위해 플래그 설정
    const isScrollingRef = { current: true };
    
    // 스크롤 위치 조정 - 타이머 추가
    setTimeout(() => {
      const carouselWidth = imageCarouselRef.current.offsetWidth;
      // console.log(`[goToPage] 스크롤 적용: left=${pageIndex * carouselWidth}`);
      
      // 스크롤 이벤트 일시적으로 비활성화
      const originalOnScroll = imageCarouselRef.current.onscroll;
      imageCarouselRef.current.onscroll = null;
      
      // 프로그래매틱 스크롤 표시
      imageCarouselRef.current.dataset.programmaticScroll = 'true';
      
      imageCarouselRef.current.scrollTo({
        left: pageIndex * carouselWidth,
        behavior: 'smooth'
      });
      
      // 스크롤 이벤트 복원 (스크롤 완료 후)
      setTimeout(() => {
        isScrollingRef.current = false;
        imageCarouselRef.current.onscroll = originalOnScroll;
        // 프로그래매틱 스크롤 표시 제거
        imageCarouselRef.current.dataset.programmaticScroll = 'false';
      }, 500); // 스크롤 애니메이션 완료 후
      
    }, 50);
  };

  // 이전 페이지로 이동
  const goToPrevPage = () => {
    // console.log(`[goToPrevPage] 호출됨: currentImagePage=${currentImagePage}`);
    if (currentImagePage > 0) {
      goToPage(currentImagePage - 1);
    }
  };

  // 다음 페이지로 이동
  const goToNextPage = () => {
    // console.log(`[goToNextPage] 호출됨: currentImagePage=${currentImagePage}, max=${generatedImageUrls.length - 1}`);
    if (currentImagePage < generatedImageUrls.length - 1) {
      goToPage(currentImagePage + 1);
    }
  };

  // 키보드 핸들러 유지
  const handleKeyDown = (e) => {
    if (generatedImageUrls.length <= 1) return;
    
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrevPage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNextPage();
    }
  };

  // 날짜 관련 부분 수정
  const getFormattedDate = () => {
    // selectedDate prop이 있으면 사용, 없으면 오늘 날짜 사용
    const dateToUse = selectedDate ? new Date(selectedDate) : new Date();
    return `${dateToUse.getFullYear()}-${String(dateToUse.getMonth() + 1).padStart(2, '0')}-${String(dateToUse.getDate()).padStart(2, '0')}`;
  };

  const formattedDate = getFormattedDate();

  // 렌더링 함수 수정: 비활성 상태일 때는 비어있는 컨테이너만 표시
  const renderContent = () => {
    // 비활성 상태이면 빈 컨테이너만 반환
    if (!isActive) {
      return <InactiveContainer>요약 이미지 비활성화됨</InactiveContainer>;
    }

    // 기존 renderContent 로직 그대로 유지
    switch (generationStatus) {
      case 'idle':
      case 'calculating':
        return ( <LoadingContainer>운동 기록 정리 중...<LoadingIndicator /></LoadingContainer> );
      case 'generating':
        return ( <LoadingContainer>이미지 생성 중... ({generationProgress.current}/{generationProgress.total})<LoadingIndicator /></LoadingContainer> );
      case 'error':
        return ( <ErrorContainer><ErrorMessage>{errorMessage || "알 수 없는 오류가 발생했습니다."}</ErrorMessage></ErrorContainer> );
      case 'complete':
        if (generatedImageUrls.length > 0) {
          return (
            <>
              <ImageCarouselContainer
                ref={imageCarouselRef}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                aspectRatio={backgroundSize.aspectRatio || 1}
                style={{ outline: 'none' }}
                onScroll={(e) => {
                  // 스크롤 이벤트 방해 방지를 위해 디바운스 처리
                  if (!imageCarouselRef.current) return;
                  
                  // 버튼으로 인한 프로그래밍적 스크롤 중에는 무시
                  if (e.currentTarget.dataset.programmaticScroll === 'true') return;
                  
                  // 스크롤 위치를 기반으로 현재 페이지 계산
                  const scrollLeft = e.currentTarget.scrollLeft;
                  const containerWidth = e.currentTarget.offsetWidth;
                  if (containerWidth <= 0) return;
                  
                  // 방향 확인을 위한 변수 추가
                  const scrollRatio = scrollLeft / containerWidth;
                  const newPage = Math.round(scrollRatio);
                  
                  // 현재 페이지가 계산된 페이지와 다를 경우 업데이트
                  if (newPage !== currentImagePage && newPage >= 0 && newPage < generatedImageUrls.length) {
                    // console.log(`[onScroll] 페이지 업데이트: ${currentImagePage} → ${newPage} (scrollRatio: ${scrollRatio.toFixed(2)})`);
                    setCurrentImagePage(newPage);
                  }
                }}
              >
                {generatedImageUrls.map((url, index) => (
                  <ImageSlide key={index} aspectRatio={backgroundSize.aspectRatio || 1}>
                    <GeneratedPngImage
                      src={url}
                      alt={`운동 요약 ${index + 1}`}
                      loading="lazy"
                      onError={(e) => console.error(`Error loading generated image ${index + 1}:`, e)}
                      draggable="false"
                    />
                  </ImageSlide>
                ))}
              </ImageCarouselContainer>
              
              {generatedImageUrls.length > 1 && (
                <NavigationButtons>
                  <NavButton 
                    onClick={(e) => {
                      e.preventDefault();
                      // console.log('[NavButton] 이전 버튼 클릭');
                      goToPrevPage();
                    }} 
                    disabled={currentImagePage === 0}
                    aria-label="이전 페이지"
                  >
                    &lt;
                  </NavButton>
                  <PageIndicatorText>
                    {currentImagePage + 1} / {generatedImageUrls.length}
                  </PageIndicatorText>
                  <NavButton 
                    onClick={(e) => {
                      e.preventDefault();
                      // console.log('[NavButton] 다음 버튼 클릭');
                      goToNextPage();
                    }}
                    disabled={currentImagePage === generatedImageUrls.length - 1}
                    aria-label="다음 페이지"
                  >
                    &gt;
                  </NavButton>
                </NavigationButtons>
              )}
            </>
          );
        } else {
           return ( <EmptyContainer>표시할 운동 요약 정보가 없습니다.</EmptyContainer> );
        }
      default: return null;
    }
  };

  return (
    <Container>
      {renderContent()}
      {(isActive && (generationStatus === 'calculating' || generationStatus === 'generating')) && selectedBackground && backgroundSize.width > 0 && (
        <HiddenDomContainer ref={domCarouselRef}>
          {pages.map((pageWorkouts, pageIndex) => (
            <SummaryCardContainerForCapture key={pageIndex} style={{ minWidth: '100%', flexShrink: 0 }} aspectRatio={backgroundSize.aspectRatio || '1/1'}>
              <BackgroundImage src={selectedBackground} alt="" crossOrigin="anonymous" loading="eager"/>
              <SummaryCardOverlay data-el="overlay">
                {pageIndex === 0 ? (
                   <>
                     <SummaryCardHeader data-el="header"><SummaryCardUserName>{user?.nickname || 'User'} | {formattedDate}</SummaryCardUserName><LogoImage src={logo} alt="" crossOrigin="anonymous" loading="eager" /></SummaryCardHeader>
                     <SummaryCardTitle data-el="title">[오늘 운동 기록]</SummaryCardTitle>
                   </>
                 ) : (
                   <>
                     <SummaryCardHeader data-el="header"><SummaryCardUserName></SummaryCardUserName><LogoImage src={logo} alt="" crossOrigin="anonymous" loading="eager" /></SummaryCardHeader>
                     <div data-el="title" style={{height: '1.5rem', flexShrink: 0, marginBottom: '.25rem'}}/>
                   </>
                  )}
                <WorkoutListContainerForCapture ref={pageIndex === 0 ? contentRef : null}>
                   {pageWorkouts && pageWorkouts.length > 0 ? pageWorkouts.map((workout, index) => {
                       const workoutIndex = pageIndex === 0 ? index : index + pages.slice(0, pageIndex).reduce((total, page) => total + (page?.length || 0), 0);
                       return (
                        <WorkoutContainer key={workout.id || `workout-${workoutIndex}`}>
                         <WorkoutItem key={workout.id || workoutIndex}>
                           <WorkoutNameRow>{workoutIndex + 1}. {workout.name} {workout.sets.length}세트</WorkoutNameRow>
                           <SetsContainer>
                            <SetItemIndicator>🚩</SetItemIndicator>
                            {workout.sets.map((set, setIndex) => (
                               <SetGroup key={setIndex}>
                                 {/* 세트 사이 화살표 */}
                                 {setIndex > 0 && (
                                   <SetArrow>→</SetArrow>
                                 )}
                                 {/* 세트 정보 묶음 */}
                                 <SetBlock>
                                   {/* 무게와 횟수가 모두 있는 경우 */}
                                   {set.weight && set.reps ? (
                                     <SetDataRow>
                                       {`${formatWeight(set.weight, workout.name)} ${formatReps(set.reps)}`} 
                                     </SetDataRow>
                                   ) : (
                                     /* 무게만 있는 경우 */
                                     set.weight && (
                                       <SetDataRow>
                                         {`${formatWeight(set.weight, workout.name)} ${formatReps(set.reps)}`} 
                                       </SetDataRow>
                                     )
                                   )}
                                   
                                   {/* 무게는 없고 횟수만 있는 경우 */}
                                   {!set.weight && set.reps && (
                                     <SetDataRow>{formatReps(set.reps)}</SetDataRow>
                                   )}
                                   
                                   {/* 시간이 있는 경우 별도로 표시 */}
                                   {set.duration && (
                                     <SetDataRow>{formatDuration(set.duration)}</SetDataRow>
                                   )}
                                 </SetBlock>
                               </SetGroup>
                             ))}
                           </SetsContainer>
                         </WorkoutItem>
                         <WorkoutDivider />
                         </WorkoutContainer>
                       );
                     }) : <NoWorkoutMessage>운동 기록 없음</NoWorkoutMessage>}
                </WorkoutListContainerForCapture>
                {pages.length > 1 && (
                  <PageIndicator>{pageIndex + 1} / {pages.length || 1}</PageIndicator>
                )}
                <SummaryCardFooter data-el="footer"><VoicefitLogo>VOICEFIT</VoicefitLogo></SummaryCardFooter>
              </SummaryCardOverlay>
            </SummaryCardContainerForCapture>
           ))}
        </HiddenDomContainer>
      )}
    </Container>
  );
};

// --- 스타일 컴포넌트 정의 (캡처 DOM 스타일 복구) ---
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 1.5rem;
  position: relative;
  min-height: 300px;
`;
const LoadingContainer = styled.div`
  height: 300px;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.gray100 || '#f1f3f5'};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.gray700 || '#495057'};
  gap: 1rem;
  padding: 1rem;
`;
const LoadingIndicator = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.gray300 || '#dee2e6'};
  border-top: 4px solid ${({ theme }) => theme.colors.primary || '#4263eb'};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
const ErrorContainer = styled(LoadingContainer)`
  background-color: ${({ theme }) => theme.colors.dangerLight || '#ffe3e3'};
  color: ${({ theme }) => theme.colors.danger || '#e03131'};
`;
const ErrorMessage = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  padding: 0 1rem;
`;
const EmptyContainer = styled(LoadingContainer)`
  background-color: ${({ theme }) => theme.colors.gray200 || '#e9ecef'};
`;
const ImageCarouselContainer = styled.div.withConfig({ shouldForwardProp: (prop) => prop !== 'aspectRatio' })`
  display: flex;
  overflow-x: hidden;
  width: 100%;
  max-width: 600px;
  flex-wrap: nowrap;
  aspect-ratio: ${props => props.aspectRatio || '1/1'};
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  
  /* 하드웨어 가속 및 성능 최적화 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: scroll-position;
  backface-visibility: hidden;
`;
const ImageSlide = styled.div.withConfig({ shouldForwardProp: (prop) => prop !== 'aspectRatio' })`
  min-width: 100%;
  width: 100%;
  flex-shrink: 0;
  aspect-ratio: ${props => props.aspectRatio || '1/1'};
  scroll-snap-align: start;
  position: relative;
  overflow: hidden;
  
  /* 하드웨어 가속 및 성능 최적화 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
`;
const GeneratedPngImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  
  /* 하드웨어 가속 및 성능 최적화 */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
`;
const HiddenDomContainer = styled.div`
  position: absolute;
  left: -9999px;
  top: 0;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  display: flex;
  width: 100%;
  max-width: 600px;
`;
const SummaryCardContainerForCapture = styled.div.withConfig({ shouldForwardProp: (prop) => prop !== 'aspectRatio' })`
  position: relative;
  overflow: hidden;
  background-color: transparent;
  aspect-ratio: ${props => props.aspectRatio || '1/1'};
  min-width: 100%;
  flex-shrink: 0;
`;
const WorkoutListContainerForCapture = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: .4rem;
  padding-bottom: .25rem;
  overflow: hidden;
  overflow-x: visible;
  height: auto;
`;
const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const SummaryCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem 2.5rem;
  color: white;

  @media (min-width: 370px) {
    padding: 2rem 3rem;
  }

  @media (min-width: 420px) {
    padding: 2rem 3.5rem;
  }
  
  @media (min-width: 555px) {
    padding: 2rem 4rem;
  }
`;
const SummaryCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: .75rem;
  position: relative;
  flex-shrink: 0;
`;
const SummaryCardUserName = styled.div`
  font-size: .75rem;
  font-weight: 500;

  @media (min-width: 370px) {
    font-size: .8rem;
  }

  @media (min-width: 420px) {
    font-size: .825rem;
  }

  @media (min-width: 555px) {
    font-size: .85rem;
  }
`;
const SummaryCardTitle = styled.div`
  font-size: .85rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: white;
  flex-shrink: 0;

  @media (min-width: 370px) {
    font-size: .9rem;
    margin-bottom: 1.1rem;
  }

  @media (min-width: 420px) {
    font-size: .95rem;
    margin-bottom: 1.25rem;
  }

  @media (min-width: 555px) {
    font-size: 1rem;
    margin-bottom: 1.15rem;
  }
`;
const WorkoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;
const WorkoutItem = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding-bottom: 0.15rem;
`;
const WorkoutDivider = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 0.35rem;
`;
const WorkoutNameRow = styled.div`
  font-size: .7rem;
  font-weight: 600;
  border-radius: 4px;
  margin-bottom: 0.1rem;
  word-break: keep-all;

  @media (min-width: 370px) {
    font-size: 0.75rem;
  }

  @media (min-width: 420px) {
    font-size: .8rem;
  }

  @media (min-width: 555px) {
    font-size: .8rem;
  }
`;
const SetsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 0.1rem;
  line-height: 1.2;
`;
const SetItemIndicator = styled.div`
  font-size: 0.5rem;
  text-align: center;
  margin-left: 0.05rem;
  margin-right: 0.1rem;

  @media (min-width: 370px) {
    font-size: 0.55rem;
  }

  @media (min-width: 420px) {
    font-size: 0.6rem;
  }
  
  @media (min-width: 555px) {
    font-size: 0.7rem;
  }
`;
const SetGroup = styled.div`
  display: flex;
  align-items: center;
  margin-right: 0.25rem;
`;
const SetBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.05rem 0rem;

  @media (min-width: 370px) {
    padding: 0.05rem 0rem;
  }

  @media (min-width: 420px) {
    padding: 0.1rem 0rem;
  }

  @media (min-width: 555px) {
    padding: 0.1rem 0rem;
  }
`;
const SetDataRow = styled.div`
  font-size: 0.55rem;
  color: #f1f3f5;
  text-align: center;
  font-weight: 400;

  @media (min-width: 370px) {
    font-size: 0.6rem;
  }

  @media (min-width: 420px) {
    font-size: 0.65rem;
  }
  
  @media (min-width: 555px) {
    font-size: 0.7rem;
  }
`;
const SetArrow = styled.div`
  margin: 0 0.25rem;
  margin-left: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.55rem;
  font-weight: bold;

  @media (min-width: 370px) {
    font-size: 0.6rem;
  }

  @media (min-width: 420px) {
    font-size: 0.65rem;
  }
  
  @media (min-width: 555px) {
    font-size: 0.7rem;
  }
`;
const NoWorkoutMessage = styled.div`
  font-size: 1.1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2rem;
`;
const SummaryCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  position: absolute;
  flex-shrink: 0;
  bottom: 1rem;
  right: 2.5rem;

  @media (min-width: 370px) {
    right: 3rem;
  }

  @media (min-width: 420px) {
    right: 3.5rem;
  }
  
  @media (min-width: 555px) {
    right: 4rem;
  }
`;
const VoicefitLogo = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  opacity: 0.7;
  letter-spacing: 1px;

  @media (min-width: 555px) {
    font-size: 0.8rem;
  }
`;
const LogoImage = styled.img`
  height: 24px;
  width: auto;
  position: absolute;
  top: -0.3rem;
  right: 0rem;

  @media (min-width: 555px) {
    top: -0.2rem;
  }
`;
const PageIndicator = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 2.5rem;
  font-size: 0.6rem;
  font-weight: 600;
  border-radius: 4px;
  opacity: 0.8;
  z-index: 2;

  @media (min-width: 370px) {
    left: 3rem;
  }

  @media (min-width: 420px) {
    left: 3.5rem;
  }
  
  @media (min-width: 555px) {
    left: 4rem;
    font-size: 0.8rem;
  }
`;

// 새로운 네비게이션 버튼 스타일
const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
  max-width: 600px;
`;

const NavButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary || '#4263eb'};
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary || '#4263eb'};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray400 || '#ced4da'};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PageIndicatorText = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray700 || '#495057'};
`;

// 비활성 상태 컨테이너 스타일 추가
const InactiveContainer = styled.div`
  height: 100px;
  width: 100%;
  max-width: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.gray100 || '#f1f3f5'};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.gray500 || '#adb5bd'};
  font-size: 0.9rem;
`;

// React.memo로 컴포넌트 감싸기 (불필요한 리렌더링 방지)
const MemoizedWorkoutSummaryImage = memo(WorkoutSummaryImage, (prevProps, nextProps) => {
  // isActive가 변경되면 리렌더링해야 함
  if (prevProps.isActive !== nextProps.isActive) {
    return false; // 리렌더링 필요
  }
  
  // 날짜가 변경되면 리렌더링해야 함
  if (prevProps.selectedDate !== nextProps.selectedDate) {
    return false; // 리렌더링 필요
  }
  
  // 비활성 상태일 때는 다른 prop 변경에 대해 리렌더링하지 않음
  if (!nextProps.isActive) {
    return true; // 리렌더링 불필요
  }
  
  // 운동 데이터가 실제로 변경되었는지 비교 (깊은 비교 대신 간단한 비교 수행)
  if (prevProps.workouts?.length !== nextProps.workouts?.length) {
    return false; // 리렌더링 필요
  }
  
  // 세트 정보 변경 여부 확인
  const prevTotalSets = prevProps.workouts?.reduce((total, w) => total + (w.sets?.length || 0), 0) || 0;
  const nextTotalSets = nextProps.workouts?.reduce((total, w) => total + (w.sets?.length || 0), 0) || 0;
  
  if (prevTotalSets !== nextTotalSets) {
    return false; // 리렌더링 필요
  }
  
  // 운동 이름이나 ID 비교
  for (let i = 0; i < (prevProps.workouts?.length || 0); i++) {
    if (prevProps.workouts[i].id !== nextProps.workouts[i].id || 
        prevProps.workouts[i].name !== nextProps.workouts[i].name) {
      return false; // 리렌더링 필요
    }
  }
  
  // 사용자 정보 변경 여부 확인
  if (prevProps.user?.nickname !== nextProps.user?.nickname) {
    return false; // 리렌더링 필요
  }
  
  return true; // 리렌더링 필요 없음 (props가 동일하거나 동등함)
});

export default MemoizedWorkoutSummaryImage;